// Top-bar language switcher.
//
// Native <select> styled to match the existing top-bar text density (small,
// neutral background). We deliberately don't use the `@ohear/ui` <Select>
// primitive here because that one is sized for forms — it's too tall for the
// header chrome. Keeping this self-contained also means the switcher renders
// before any tailwind/css from the design system loads, which matters for the
// very-first paint.

import { type Locale, isLocale } from '@ohear/shared'
import type { CSSProperties } from 'react'
import { useUpdateLocale } from '../lib/api/account'
import { useAuth } from '../lib/auth'
import { useLocale } from '../lib/i18n/locale-context'

/** Native-name labels — always rendered in the OWN language so users can
 *  recognise their language even when the UI is currently in another one. */
const LABELS: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  zh: '中文',
}

export function LanguageSwitcher() {
  const { locale, setLocale, supported } = useLocale()
  const { session } = useAuth()
  const updateLocale = useUpdateLocale()

  return (
    <select
      value={locale}
      onChange={(e) => {
        const next = e.target.value
        if (!isLocale(next)) return
        setLocale(next)
        // Fire-and-forget DB sync when signed in. localStorage is the source
        // of truth on this device; the server copy only matters when the user
        // signs in on a fresh device. Errors are swallowed deliberately —
        // failing to persist a UI preference must never surface as a toast.
        if (session) {
          updateLocale.mutate(next, { onError: () => {} })
        }
      }}
      aria-label="Language / 语言"
      style={selectStyle}
    >
      {supported.map((l) => (
        <option key={l} value={l}>
          {LABELS[l] ?? l}
        </option>
      ))}
    </select>
  )
}

const selectStyle: CSSProperties = {
  fontSize: 12,
  padding: '4px 22px 4px 8px',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  background: '#fff',
  color: '#4b5563',
  cursor: 'pointer',
  appearance: 'none',
  // Subtle caret to match the rest of the top bar.
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%239ba3af' stroke-width='1.5'><path d='M4 6l4 4 4-4'/></svg>\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 6px center',
  backgroundSize: 12,
}
